import { translateBackend } from '@assets/i18n/config';
import type { FetchWorkflowsResponse } from '@generated/graphql';
import { WorkflowStates } from '@interfaces/workflow';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import { FETCH_WORKFLOWS } from '@utils/graphql/fetchWorkflows';
import { useFetchAllPages } from '@utils/useFetchAllPages';
import { kebabCase } from 'lodash-es';
import { useContext } from 'react';

type WorkflowItem = FetchWorkflowsResponse['workflows'][number];

export const WorkflowSelect = ({
  onChange,
  value,
  label,
  disabled = false,
  includeEmpty = true,
  emptyLabel = '-',
  testId,
  onBlur,
  error,
  organizationId,
  skip = false,
}: {
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  value: string;
  label: string;
  disabled?: boolean;
  includeEmpty?: boolean;
  emptyLabel?: string;
  testId?: string;
  onBlur?: () => void;
  error?: boolean;
  organizationId?: string;
  skip?: boolean;
}) => {
  const { activeOrganization } = useContext(UserContext);

  const { data: workflows, loading } = useFetchAllPages<
    { fetchWorkflows: FetchWorkflowsResponse },
    WorkflowItem
  >({
    query: FETCH_WORKFLOWS,
    variables: {
      input: {
        filter: {
          organizationId: organizationId ?? activeOrganization.id,
          state: WorkflowStates.ACTIVE,
          excludeRoleProfileFilter: true,
        },
      },
    },
    extractPage: (data) => ({
      items: data.fetchWorkflows?.workflows ?? [],
      totalCount: data.fetchWorkflows?.totalCount,
    }),
    skip,
  });

  return (
    <SelectField
      fullWidth
      value={value}
      label={label}
      onChange={onChange}
      disabled={disabled || loading}
      testId={testId ?? 'workflow-select'}
      onBlur={onBlur}
      error={error}
    >
      {includeEmpty && (
        <MenuItem key='empty' value=''>
          {emptyLabel}
        </MenuItem>
      )}
      {loading ? (
        <MenuItem>...</MenuItem>
      ) : (
        workflows?.map((workflow) => (
          <MenuItem
            data-testId={
              testId
                ? `${testId}-${kebabCase(workflow.name.en ?? workflow.id)}`
                : `workflow-select-${kebabCase(workflow.name.en ?? workflow.id)}`
            }
            key={workflow.id}
            value={workflow.id}
          >
            {translateBackend(workflow.name)}
          </MenuItem>
        ))
      )}
    </SelectField>
  );
};
